import { useEffect, useRef, useState } from 'react';
import type { Side } from '../hl/outcomes';
import { useT } from '../i18n/useT';
import MarketView, { type MarketVariant } from '../market/MarketView';

/** Events that mean the user is scrolling or typing: the panel stops scrolling itself into view. */
const USER_SCROLL = ['wheel', 'touchmove', 'keydown', 'mousedown'] as const;

export interface InlineMarketProps {
  outcome: number;
  /** `wide` under a row of tiles, `narrow` in a column. */
  variant: Exclude<MarketVariant, 'page'>;
  /** Search string of the full-page link (e.g. "?ghost=0x…"), '' for none. */
  linkSearch: string;
  /** True beside the list: the column is sticky, so opening it never scrolls the page. */
  beside: boolean;
  /** #2242: fills the height of its column (compact list on a desktop) and scrolls inside if it ever has to. */
  fill?: boolean;
  onClose: () => void;
}

/**
 * Depth, trades and order entry of one outcome inside Markets (#1953): MarketView with a link to the full page and a
 * close button. Mount one per outcome (key it): the book, tape and ticket draft start fresh. Takes the focus when it
 * opens, so the keyboard continues inside it.
 */
export default function InlineMarket({ outcome, variant, linkSearch, beside, fill = false, onClose }: InlineMarketProps) {
  const t = useT();
  const [side, setSide] = useState<Side>(0);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => ref.current?.focus({ preventScroll: true }), []);
  useEffect(() => {
    // Whole panel if it fits, else its top; the selected tile stays in view above it when there is room.
    const el = ref.current;
    if (beside || !el) return;
    const reveal = () => el.scrollIntoView?.({ block: 'nearest' });
    reveal();
    // The book and ticket fill in after the first frame and the panel grows, and tiles above it can grow too (prices,
    // volumes): keep revealing it for a moment, so the whole order ticket ends up on screen (#2242). The first wheel,
    // touch, click or key from the user hands scrolling back.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(reveal);
    ro.observe(el);
    ro.observe(document.body);
    const stop = () => {
      ro.disconnect();
      clearTimeout(timer);
      for (const ev of USER_SCROLL) window.removeEventListener(ev, stop);
    };
    const timer = setTimeout(stop, 1500);
    for (const ev of USER_SCROLL) window.addEventListener(ev, stop, { passive: true });
    return stop;
  }, [beside]);
  const q = new URLSearchParams(linkSearch);
  if (side === 1) q.set('side', '1');
  const search = q.toString();
  // #2242: no row of its own for the full-page link and the close button: the link ends the header's chip row, the ✕
  // follows the ticket's "How does this work?" (or ends the chip row while the ticket is under the book).
  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-label={t('catalog.panel_aria', { id: String(outcome) })}
      data-testid="market-panel"
      data-outcome={outcome}
      className={`card scroll-mt-28 border-accent/60 p-3 outline-none sm:p-4 ${fill ? 'h-full overflow-y-auto' : ''}`}
    >
      <MarketView
        id={outcome}
        raw={String(outcome)}
        side={side}
        onSide={setSide}
        variant={variant}
        pageHref={`/market/${outcome}${search ? `?${search}` : ''}`}
        onClose={onClose}
      />
    </section>
  );
}
